import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { hash } from "@ember/helper";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import PluginOutlet from "discourse/components/plugin-outlet";
import TopicMapLink from "discourse/components/topic-map/topic-map-link";
import TopicParticipants from "discourse/components/topic-map/topic-participants";
import TopicViews from "discourse/components/topic-map/topic-views";
import TopicViewsChart from "discourse/components/topic-map/topic-views-chart";
import DMenu from "discourse/float-kit/components/d-menu";
import lazyHash from "discourse/helpers/lazy-hash";
import { ajax } from "discourse/lib/ajax";
import { emojiUnescape } from "discourse/lib/text";
import DButton from "discourse/ui-kit/d-button";
import DConditionalLoadingSpinner from "discourse/ui-kit/d-conditional-loading-spinner";
import dBoundAvatarTemplate from "discourse/ui-kit/helpers/d-bound-avatar-template";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import dNumber from "discourse/ui-kit/helpers/d-number";
import { i18n } from "discourse-i18n";

const TRUNCATED_LINKS_LIMIT = 5;
const LINKS_THRESHOLD = 50;
const MIN_POST_READ_TIME_MINUTES = 4;
const MIN_READ_TIME_MINUTES = 3;
const MIN_LIKES_COUNT = 5;
const MIN_PARTICIPANTS_COUNT = 5;
const MIN_USERS_COUNT_FOR_AVATARS = 2;

export const MIN_POSTS_COUNT = 3;

export default class TopicMapSummary extends Component {
  @service site;
  @service siteSettings;
  @service mapCache;
  @service dialog;

  @tracked allLinksShown = false;
  @tracked top3LikedPosts = [];
  @tracked views = [];
  @tracked loading = true;

  get shouldShowParticipants() {
    return (
      this.args.topic.posts_count >= MIN_POSTS_COUNT &&
      this.args.topicDetails.participants?.length >=
        MIN_USERS_COUNT_FOR_AVATARS &&
      this.site.desktopView
    );
  }

  get first5Participants() {
    return this.args.topicDetails.participants.slice(0, MIN_PARTICIPANTS_COUNT);
  }

  get readTimeMinutes() {
    const calculatedTime = Math.ceil(
      Math.max(
        this.args.topic.word_count / this.siteSettings.read_time_word_count,
        (this.args.topic.posts_count * MIN_POST_READ_TIME_MINUTES) / 60
      )
    );

    return calculatedTime > MIN_READ_TIME_MINUTES ? calculatedTime : null;
  }

  get topRepliesSummaryEnabled() {
    return this.args.postStream?.summary;
  }

  get shouldShowReplyCount() {
    return this.args.topic.is_nested_view;
  }

  get replyCount() {
    return this.args.topic.replyCount;
  }

  get topRepliesTitle() {
    if (this.topRepliesSummaryEnabled) {
      return;
    }

    return i18n("summary.short_title");
  }

  get topRepliesIcon() {
    return this.topRepliesSummaryEnabled ? "up-down" : "layer-group";
  }

  get topRepliesLabel() {
    return this.topRepliesSummaryEnabled
      ? i18n("summary.show_all_label")
      : i18n("summary.short_label");
  }

  get loneStat() {
    if (this.args.topic.has_summary || this.shouldShowReplyCount) {
      return false;
    }
    return [this.hasLikes, this.hasUsers, this.hasLinks].every((stat) => !stat);
  }

  get manyStats() {
    return [this.hasLikes, this.hasUsers, this.hasLinks].every(Boolean);
  }

  get minViewsCount() {
    return Math.max(this.args.topic.views, 1);
  }

  get shouldShowViewsChart() {
    return this.views.stats.length > 2;
  }

  get linksCount() {
    return this.args.topicDetails.links?.length ?? 0;
  }

  get topicLinks() {
    return this.args.topicDetails.links;
  }

  get linksToShow() {
    return this.allLinksShown
      ? this.topicLinks
      : this.topicLinks?.slice(0, TRUNCATED_LINKS_LIMIT);
  }

  get hasMoreLinks() {
    return !this.allLinksShown && this.linksCount > TRUNCATED_LINKS_LIMIT;
  }

  get hasLikes() {
    return (
      this.args.topic.like_count > MIN_LIKES_COUNT &&
      this.args.topic.posts_count > MIN_POSTS_COUNT
    );
  }

  get hasUsers() {
    return this.args.topic.participant_count > MIN_PARTICIPANTS_COUNT;
  }

  get hasLinks() {
    return this.linksCount > 0;
  }

  @action
  showAllLinks() {
    this.allLinksShown = true;
  }

  @action
  showTopReplies() {
    this.args.postStream?.showTopReplies();
  }

  @action
  cancelFilter() {
    this.args.postStream?.cancelFilter();
    this.args.postStream?.refresh();
  }

  @action
  postUrl(post) {
    return this.args.topic.urlForPostNumber(post.post_number);
  }

  @action
  fetchMostLiked() {
    const cacheKey = `top3LikedPosts_${this.args.topic.id}`;
    const cachedData = this.mapCache.get(cacheKey);
    this.loading = true;

    if (cachedData) {
      this.top3LikedPosts = cachedData;
      this.loading = false;
      return;
    }

    const filter = `/search.json?q=" " topic%3A${this.args.topic.id} order%3Alikes`;

    ajax(filter)
      .then((data) => {
        const top3LikedPosts = data.posts
          .filter((post) => post.post_number > 1 && post.like_count > 0)
          .sort((a, b) => b.like_count - a.like_count)
          .slice(0, 3);

        this.mapCache.set(cacheKey, top3LikedPosts);
        this.top3LikedPosts = top3LikedPosts;
      })
      .catch((error) => {
        this.dialog.alert(
          i18n("generic_error_with_reason", {
            error: `http: ${error.status} - ${error.body}`,
          })
        );
      })
      .finally(() => {
        this.loading = false;
      });
  }

  @action
  fetchViews() {
    const cacheKey = `topicViews_${this.args.topic.id}`;
    const cachedData = this.mapCache.get(cacheKey);
    this.loading = true;

    if (cachedData) {
      this.views = cachedData;
      this.loading = false;
      return;
    }

    ajax(`/t/${this.args.topic.id}/view-stats.json`)
      .then((data) => {
        if (data.stats.length) {
          this.views = data;
        } else {
          data.stats.push({
            viewed_at: new Date().toISOString().split("T")[0],
            views: this.args.topic.views,
          });
          this.views = data;
        }
        this.mapCache.set(cacheKey, data);
      })
      .catch((error) => {
        this.dialog.alert(
          i18n("generic_error_with_reason", {
            error: `http: ${error.status} - ${error.body}`,
          })
        );
      })
      .finally(() => {
        this.loading = false;
      });
  }

  <template>
    <div
      class={{dConcatClass
        "topic-map__stats"
        (if this.loneStat "--single-stat")
        (if this.manyStats "--many-stats")
      }}
    >
      <DMenu
        @arrow={{true}}
        @groupIdentifier="topic-map"
        @identifier="topic-map__views"
        @inline={{true}}
        @interactive={{true}}
        @modalForMobile={{true}}
        @onShow={{this.fetchViews}}
        @placement="right"
      >
        <:trigger>
          {{dNumber this.minViewsCount noTitle="true"}}
          <span class="topic-map__stat-label">
            {{i18n "views_lowercase" count=this.minViewsCount}}
          </span>
        </:trigger>
        <:content>
          <h3>{{i18n "topic_map.menu_titles.views"}}</h3>
          <DConditionalLoadingSpinner @condition={{this.loading}}>
            {{#if this.shouldShowViewsChart}}
              <TopicViewsChart
                @created={{@topic.created_at}}
                @views={{this.views}}
              />
            {{else}}
              <TopicViews @views={{this.views}} />
            {{/if}}
          </DConditionalLoadingSpinner>
        </:content>
      </DMenu>

      {{#if this.shouldShowReplyCount}}
        <div class="topic-map__stat topic-map__replies">
          {{dNumber this.replyCount noTitle="true"}}
          <span class="topic-map__stat-label">
            {{i18n "replies_lowercase" count=this.replyCount}}
          </span>
        </div>
      {{/if}}

      {{#if this.hasLikes}}
        {{#if this.site.can_search}}
          <DMenu
            @arrow={{true}}
            @autofocus={{true}}
            @groupIdentifier="topic-map"
            @identifier="topic-map__likes"
            @inline={{true}}
            @interactive={{true}}
            @modalForMobile={{true}}
            @placement="right"
          >
            <:trigger>
              {{dNumber @topic.like_count noTitle="true"}}
              <span class="topic-map__stat-label">
                {{i18n "likes_lowercase" count=@topic.like_count}}
              </span>
            </:trigger>
            <:content>
              <h3 {{didInsert this.fetchMostLiked}}>{{i18n
                  "topic_map.menu_titles.replies"
                }}</h3>
              <DConditionalLoadingSpinner @condition={{this.loading}}>
                <PluginOutlet
                  @name="most-liked-replies"
                  @outletArgs={{lazyHash
                    posts=this.top3LikedPosts
                    postUrl=this.postUrl
                  }}
                >
                  <ul>
                    {{#each this.top3LikedPosts as |post|}}
                      <li>
                        <a href={{this.postUrl post}}>
                          <span class="like-section__user">
                            {{dBoundAvatarTemplate
                              post.avatar_template
                              "tiny"
                              (hash title=post.username)
                            }}
                            {{post.username}}
                          </span>
                          <span class="like-section__likes">
                            {{post.like_count}}
                            {{dIcon "heart"}}</span>
                          <p>
                            {{trustHTML (emojiUnescape post.blurb)}}
                          </p>
                        </a>
                      </li>
                    {{/each}}
                  </ul>
                </PluginOutlet>
              </DConditionalLoadingSpinner>
            </:content>
          </DMenu>
        {{else}}
          <div class="topic-map__stat topic-map__likes">
            {{dNumber @topic.like_count noTitle="true"}}
            <span class="topic-map__stat-label">
              {{i18n "likes_lowercase" count=@topic.like_count}}
            </span>
          </div>
        {{/if}}
      {{/if}}

      {{#if this.linksCount}}
        <DMenu
          @arrow={{true}}
          @autofocus={{true}}
          @groupIdentifier="topic-map"
          @identifier="topic-map__links"
          @inline={{true}}
          @interactive={{true}}
          @modalForMobile={{true}}
          @placement="right"
        >
          <:trigger>
            {{dNumber
              this.linksCount
              maxDisplay=LINKS_THRESHOLD
              noTitle="true"
            }}
            <span class="topic-map__stat-label">
              {{i18n "links_lowercase" count=this.linksCount}}
            </span>
          </:trigger>
          <:content>
            <h3>{{i18n "topic_map.links_title"}}</h3>
            <ul class="topic-links">
              {{#each this.linksToShow as |link|}}
                <li>
                  <TopicMapLink
                    @attachment={{link.attachment}}
                    @clickCount={{link.clicks}}
                    @rootDomain={{link.root_domain}}
                    @title={{link.title}}
                    @url={{link.url}}
                    @userId={{link.user_id}}
                  />
                </li>
              {{/each}}

            </ul>
            {{#if this.hasMoreLinks}}
              <DButton
                class="link-summary btn-flat"
                @action={{this.showAllLinks}}
                @icon="chevron-down"
                @title="topic_map.links_shown"
              />

            {{/if}}
          </:content>
        </DMenu>
      {{/if}}

      {{#if this.hasUsers}}
        <DMenu
          @arrow={{true}}
          @autofocus={{true}}
          @groupIdentifier="topic-map"
          @identifier="topic-map__users"
          @inline={{true}}
          @interactive={{true}}
          @modalForMobile={{true}}
          @placement="right"
        >
          <:trigger>
            {{dNumber @topic.participant_count noTitle="true"}}
            <span class="topic-map__stat-label">
              {{i18n "users_lowercase" count=@topic.participant_count}}
            </span>
          </:trigger>
          <:content>
            <TopicParticipants
              @participants={{@topicDetails.participants}}
              @title={{i18n "topic_map.participants_title"}}
              @userFilters={{@postStream.userFilters}}
            />
          </:content>
        </DMenu>
      {{/if}}

      {{#if this.shouldShowParticipants}}
        <TopicParticipants
          @participants={{this.first5Participants}}
          @userFilters={{@postStream.userFilters}}
        />
      {{/if}}
      <PluginOutlet
        @name="topic-map-participants-after"
        @outletArgs={{lazyHash topic=@topic}}
      />
      <div class="topic-map__buttons">
        {{#if this.readTimeMinutes}}
          <div class="estimated-read-time">
            <span> {{i18n "topic_map.read"}} </span>
            <span>
              {{this.readTimeMinutes}}
              {{i18n "topic_map.minutes"}}
            </span>
          </div>
        {{/if}}
        {{#if @topic.has_summary}}
          <div class="summarization-button">
            <DButton
              class="btn-default top-replies"
              @action={{if
                @postStream.summary
                this.cancelFilter
                this.showTopReplies
              }}
              @icon={{this.topRepliesIcon}}
              @translatedLabel={{this.topRepliesLabel}}
              @translatedTitle={{this.topRepliesTitle}}
            />
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
